import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { EpisodeParticipantRole, EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'

import { useAllEpisodesForSelectedClient } from './useAllEpisodesForSelectedClient'
import { useEpisodesForSelectedClient } from './useEpisodesForSelectedClient'
import { getAuthConfig, getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { isActiveParticipantForEpisode } from '../episodeParticipants'
import { pickEpisode } from '../pickEpisode'

export const useCurrentEpisodeForClient = (episodeId?: string) => {
  const { data: providerEpisodes, isLoading: loadingProviderEpisodes } = useEpisodesForSelectedClient()
  const { data: allEpisodes, isLoading: loadingAllEpisodes } = useAllEpisodesForSelectedClient()
  const selectedClientId = useSelector(getSelectedClientId)
  const { isDBTTeensEnabled } = useFlags()
  const openClientAccessSupported = useSelector(getAuthConfig)?.openClientAccessSupported ?? false
  const providerId = useSelector(getAuthUserId)

  const episodes = useMemo(() => {
    // The provider-scoped API only returns owned episodes; care-team episodes follow so an owned one is picked first.
    const ownEpisodes = providerEpisodes ?? []
    const careTeamEpisodes =
      allEpisodes?.filter(
        (episode) =>
          episode.provider_id !== providerId &&
          isActiveParticipantForEpisode(episode, providerId, EpisodeParticipantRole.CARE_TEAM_PROVIDER),
      ) ?? []
    const base = careTeamEpisodes.length ? [...ownEpisodes, ...careTeamEpisodes] : ownEpisodes
    if (!openClientAccessSupported || !isDBTTeensEnabled) {
      return base
    }
    const openClientEpisode = allEpisodes?.find(
      (episode) =>
        episode.provider_id !== providerId &&
        episode.state === EpisodeStates.IN_PROGRESS &&
        episode.program_name === ProgramNames.DialecticalBehaviorTherapyTeens,
    )
    return openClientEpisode ? [openClientEpisode, ...base] : base
  }, [providerEpisodes, allEpisodes, openClientAccessSupported, isDBTTeensEnabled, providerId])

  const currentEpisode = useMemo(
    () => pickEpisode(episodes, selectedClientId, episodeId),
    [episodeId, selectedClientId, episodes],
  )
  return {
    currentEpisode,
    loadingEpisodes: loadingProviderEpisodes || loadingAllEpisodes,
  }
}
